import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../common/Loader";

const AddAboutPageModal = ({ isOpen, onClose, onContentAdded }) => {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [keyPoints, setKeyPoints] = useState([{ heading: "", points: [""] }]);
  const [loading, setLoading] = useState(false);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);
    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
      formData
    );
    return res.data.secure_url;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const urls = await Promise.all(Array.from(files).map(uploadToCloudinary));
      setImages([...images, ...urls]);
      toast.success("Images uploaded successfully");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title,
        subtitle,
        description: description.split("\n").filter(d => d.trim()),
        images,
        keyPoints: keyPoints.map(kp => ({ heading: kp.heading, points: kp.points.filter(p => p) }))
      };
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/about/create-about`, payload);
      onContentAdded(res?.data?.data);
      toast.success("About Page added");
      onClose();
      // Reset
      setTitle(""); setSubtitle(""); setDescription(""); setImages([]); setKeyPoints([{ heading: "", points: [""] }]);
    } catch {
      toast.error("Failed to add About Page content");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-10 z-50 overflow-auto">
      <div className="bg-white w-full max-w-3xl p-6 rounded relative">
        {/* Top-right Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl font-bold"
        >
          ×
        </button>

        <h2 className="text-xl font-bold mb-4">Add About Page Content</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} className="w-full border px-3 py-2 rounded" required />
          <input type="text" placeholder="Subtitle" value={subtitle} onChange={e => setSubtitle(e.target.value)} className="w-full border px-3 py-2 rounded" required />
          <textarea placeholder="Description (one per line)" value={description} onChange={e => setDescription(e.target.value)} className="w-full border px-3 py-2 rounded" rows={3} />
          
          <input type="file" multiple onChange={handleImageUpload} />
          <div className="flex flex-wrap gap-2">
            {images.map((img, i) => (
              <div key={i} className="relative w-24 h-24">
                <img src={img} className="w-full h-full object-cover rounded border" />
                <button
                  type="button"
                  onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* Key Points Section */}
          {keyPoints.map((kp, i) => (
            <div key={i} className="mb-2 border p-2 rounded relative">
              {/* Delete Section Button */}
              {keyPoints.length > 1 && (
                <button
                  type="button"
                  onClick={() => setKeyPoints(keyPoints.filter((_, idx) => idx !== i))}
                  className="absolute top-1 right-1 text-red-500 text-sm"
                >
                  ×
                </button>
              )}

              <input type="text" placeholder="Heading" value={kp?.heading} onChange={e => { const temp = [...keyPoints]; temp[i].heading = e.target.value; setKeyPoints(temp); }} className="w-full border px-2 py-1 mb-1 rounded" />
              {kp?.points.map((p, j) => (
                <div key={j} className="flex gap-2 mb-1">
                  <input type="text" placeholder="Point" value={p} onChange={e => { const temp = [...keyPoints]; temp[i].points[j] = e.target.value; setKeyPoints(temp); }} className="border px-2 py-1 flex-1 rounded" />
                  {kp?.points.length > 1 && (
                    <button type="button" className="text-red-500" onClick={() => { const temp = [...keyPoints]; temp[i].points = temp[i].points.filter((_, idx) => idx !== j); setKeyPoints(temp); }}>×</button>
                  )}
                </div>
              ))}
              <button type="button" className="text-blue-500 text-sm" onClick={() => { const temp = [...keyPoints]; temp[i].points.push(""); setKeyPoints(temp); }}>+ Add Point</button>
            </div>
          ))}
          <button type="button" className="bg-blue-500 text-white px-2 py-1 rounded text-sm" onClick={() => setKeyPoints([...keyPoints, { heading: "", points: [""] }])}>+ Add Key Point Section</button>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
            <button type="submit" disabled={loading} className="bg-orange-500 px-4 py-2 text-white rounded">{loading ? <Loader /> : "Add Content"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAboutPageModal;
